/**
 * Selection maths — pure, no React, no DOM.
 *
 * Pick MANY (rows): `DataTable`'s checkbox column, its "select all matching"
 * banner and `BulkActionBar` all reduce to the same few set operations; the
 * whole banner/header derivation is `selectionSummary` and the callbacks each
 * gesture fires are `selectionIntent`. Pick ONE (a listbox): `EntitySelector`'s
 * highlight and Enter rules and its whole search state machine
 * (`pickerReducer`), at the bottom.
 *
 * They live here so the table, a card gallery that selects the same way
 * (Marketplaces) and the migration wizard's work lists agree on what "all",
 * "some" and "none" mean — and so the rules are pinned by a test
 * (`tenant/frontend/hooks/selection.test.ts`) rather than re-derived, untested,
 * inside a `.tsx` the Node runner cannot import.
 *
 * Every function returns a NEW `Set`: selection is React state, and mutating
 * the set the component holds would skip the re-render.
 */

export type SelectionKey = string | number;

/** State of a "select all" checkbox over a list of keys. */
export type TriState = 'none' | 'some' | 'all';

/**
 * `all` when every key is selected, `none` when none is (or there are no keys
 * at all — an empty page has nothing to tick), `some` otherwise.
 *
 * The keys are the SELECTABLE rows the caller is talking about — for the
 * header checkbox, the selectable rows on the current page. A row that cannot
 * be selected must not keep the header from reaching `all`.
 */
export function triState(keys: readonly SelectionKey[], selected: ReadonlySet<SelectionKey>): TriState {
    if (keys.length === 0) return 'none';
    let hits = 0;
    for (const k of keys) if (selected.has(k)) hits += 1;
    if (hits === 0) return 'none';
    return hits === keys.length ? 'all' : 'some';
}

/** Flip one key. */
export function toggleKey<K extends SelectionKey>(selected: ReadonlySet<K>, key: K): Set<K> {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
}

/** Union. */
export function addKeys<K extends SelectionKey>(selected: ReadonlySet<K>, keys: Iterable<K>): Set<K> {
    const next = new Set(selected);
    for (const k of keys) next.add(k);
    return next;
}

/** Difference. */
export function removeKeys<K extends SelectionKey>(selected: ReadonlySet<K>, keys: Iterable<K>): Set<K> {
    const next = new Set(selected);
    for (const k of keys) next.delete(k);
    return next;
}

/**
 * What a click on the header checkbox does: a page that is fully selected is
 * cleared; a page with none or SOME selected becomes fully selected — the same
 * thing a native indeterminate checkbox does (it turns checked on click).
 * Keys outside `pageKeys` (other pages) are never touched.
 */
export function togglePage<K extends SelectionKey>(selected: ReadonlySet<K>, pageKeys: readonly K[]): Set<K> {
    return triState(pageKeys, selected) === 'all' ? removeKeys(selected, pageKeys) : addKeys(selected, pageKeys);
}

/**
 * Keep only the keys that still exist (after a reload or a filter). Returns the
 * SAME instance when nothing was dropped, so a caller can write
 * `setSelected(prev => retainKeys(prev, ids))` inside an effect without
 * re-rendering forever.
 */
export function retainKeys<K extends SelectionKey>(selected: Set<K>, valid: Iterable<SelectionKey>): Set<K> {
    const keep: ReadonlySet<SelectionKey> = valid instanceof Set ? (valid as ReadonlySet<SelectionKey>) : new Set(valid);
    let dropped = false;
    for (const k of selected) {
        if (!keep.has(k)) { dropped = true; break; }
    }
    if (!dropped) return selected;
    const next = new Set<K>();
    for (const k of selected) if (keep.has(k)) next.add(k);
    return next;
}

/** How many of `keys` are selected. */
export function countSelected(keys: Iterable<SelectionKey>, selected: ReadonlySet<SelectionKey>): number {
    let n = 0;
    for (const k of keys) if (selected.has(k)) n += 1;
    return n;
}

/**
 * The "N selected · select all M matching" banner above a selectable table.
 *
 * - `hidden` — nothing to say: the matching set is no bigger than the page, or
 *   the page is not fully selected yet.
 * - `offer`  — the whole page is selected and more rows match than the page
 *   shows: offer to extend the selection to all of them.
 * - `all`    — every matching row is selected: say so and offer to clear.
 *
 * `matchingCount` is the number of SELECTABLE rows that match the current
 * filter across all pages (the server's `total` for a server-paged list, the
 * filtered in-memory list for a client-paged one). `null` = unknown → hidden.
 */
export type MatchingBannerState = 'hidden' | 'offer' | 'all';

export function matchingBanner(input: {
    pageState: TriState;
    pageSelectableCount: number;
    matchingCount: number | null | undefined;
    allMatchingSelected: boolean;
    canSelectAllMatching: boolean;
}): MatchingBannerState {
    const { pageState, pageSelectableCount, matchingCount, allMatchingSelected, canSelectAllMatching } = input;
    if (matchingCount == null || matchingCount <= pageSelectableCount) return 'hidden';
    if (allMatchingSelected) return 'all';
    if (pageState === 'all' && canSelectAllMatching) return 'offer';
    return 'hidden';
}

/**
 * What only the page knows on a SERVER-paged list: the server's count of
 * selectable rows matching the filter, and whether «all matching» is on — a
 * FLAG the page keeps (and sends to the bulk endpoint), never something the
 * table can derive from a set of keys that only ever holds loaded rows.
 */
export interface ServerMatching {
    matchingCount: number;
    allMatchingSelected: boolean;
    /** Selected rows within the current filter, when the page knows it.
     *  Default: the selected rows of the current page. */
    selectedMatchingCount?: number;
}

export interface SelectionSummaryInput {
    /** Selectable rows of the current page the user can see. */
    pageSelectableKeys: readonly SelectionKey[];
    selectedKeys: ReadonlySet<SelectionKey>;
    /** In-memory list: every selectable row matching the filter, all pages.
     *  `null` on a server-paged list (then `server` speaks). */
    matchingKeys: readonly SelectionKey[] | null;
    server: ServerMatching | null;
    /** The page offers bulk selection at all (DataTable's `showSelectAll`).
     *  `false` hides the banner too: hiding the header checkbox and then
     *  offering «select all N» one click later would undo the point of hiding
     *  it (the catalogue picker hides both until a filter narrows the list). */
    showSelectAll: boolean;
}

export interface SelectionSummary {
    /** The selection as it must be DRAWN. Equal to `selectedKeys`, except on a
     *  server-paged list with «all matching» on: the visible page then counts
     *  as selected although its keys need not be in the set. */
    effective: ReadonlySet<SelectionKey>;
    /** Header checkbox over `pageSelectableKeys`. */
    pageState: TriState;
    matchingCount: number;
    allMatchingSelected: boolean;
    banner: MatchingBannerState;
    /** The `{count}` of the banner: selected rows WITHIN the matching set —
     *  never keys a filter hides, never more than `matchingCount`. */
    bannerCount: number;
}

/**
 * Everything DataTable shows about a selection, in one place.
 *
 * - In memory, «all matching» is derived by counting the selected keys INSIDE
 *   the matching set; keys a filter hides (kept on purpose, see `retainKeys`)
 *   never inflate the count nor fake an «all».
 * - Server-paged, it is the page's flag, and the count is the page's number
 *   (or the selected rows on this page) — never `selectedKeys.size`, which
 *   mixes in keys from other filters.
 */
export function selectionSummary(input: SelectionSummaryInput): SelectionSummary {
    const { pageSelectableKeys: page, selectedKeys, matchingKeys, server, showSelectAll } = input;
    const serverAll = !matchingKeys && !!server?.allMatchingSelected;
    const effective: ReadonlySet<SelectionKey> = serverAll ? addKeys(selectedKeys, page) : selectedKeys;
    const pageState = triState(page, effective);

    let matchingCount: number;
    let allMatchingSelected: boolean;
    let selectedInMatching: number;
    if (matchingKeys) {
        matchingCount = matchingKeys.length;
        selectedInMatching = countSelected(matchingKeys, selectedKeys);
        allMatchingSelected = matchingCount > 0 && selectedInMatching >= matchingCount;
    } else if (server) {
        matchingCount = Math.max(0, server.matchingCount);
        allMatchingSelected = server.allMatchingSelected;
        selectedInMatching = allMatchingSelected
            ? matchingCount
            : (server.selectedMatchingCount ?? countSelected(page, selectedKeys));
    } else {
        matchingCount = page.length;
        selectedInMatching = countSelected(page, selectedKeys);
        allMatchingSelected = matchingCount > 0 && selectedInMatching >= matchingCount;
    }
    selectedInMatching = Math.min(Math.max(0, selectedInMatching), matchingCount);

    const banner = showSelectAll
        ? matchingBanner({
            pageState,
            pageSelectableCount: page.length,
            matchingCount,
            allMatchingSelected,
            canSelectAllMatching: !!matchingKeys || !!server,
        })
        : 'hidden';
    return {
        effective,
        pageState,
        matchingCount,
        allMatchingSelected,
        banner,
        bannerCount: allMatchingSelected ? matchingCount : selectedInMatching,
    };
}

/**
 * Which flavour of selection a DataTable was given. Server-paged is told by
 * `matchingCount` being PRESENT — a server total of `0` is still a server
 * total, so this is never a truthiness check.
 */
export type SelectionMode = 'memory' | 'server';

export function selectionMode(selection: { matchingCount?: number | undefined }): SelectionMode {
    return selection.matchingCount !== undefined ? 'server' : 'memory';
}

/** A user gesture on a selectable table. */
export type SelectionIntent =
    | { kind: 'row'; key: SelectionKey }
    | { kind: 'page'; keys: readonly SelectionKey[] }
    | { kind: 'selectAllMatching' }
    | { kind: 'clearAllMatching' };

export interface SelectionIntentContext {
    mode: SelectionMode;
    selectedKeys: ReadonlySet<SelectionKey>;
    /** What the rows draw (`selectionSummary().effective`). */
    effective: ReadonlySet<SelectionKey>;
    /** In memory: every selectable row matching the filter, all pages. */
    matchingKeys: readonly SelectionKey[] | null;
    /** Server-paged: the page's «all matching» FLAG (never a derived value). */
    allMatchingSelected: boolean;
}

/**
 * What DataTable must call, in this order: `onClearAllMatching()`, then
 * `onSelectAllMatching()`, then `onChange(next)` when `next` is not null.
 */
export interface SelectionEffects {
    clearAllMatching: boolean;
    selectAllMatching: boolean;
    next: Set<SelectionKey> | null;
}

/**
 * Turn a gesture into the callbacks DataTable fires — the one place that
 * decides them, so the table only dispatches.
 *
 * - A hand change (a row, the header) works on what is DRAWN. Under the
 *   server flag that is the page made explicit: the flag drops first, then the
 *   page minus the change is sent — unticking one row leaves the rest ticked.
 * - «Select all matching»: the server flag on a server-paged list; in memory,
 *   every matching key joins the set.
 * - The banner's «clear» clears the MATCHING set only: in memory, keys a
 *   filter hides stay selected (the bulk bar keeps counting them); server-paged,
 *   only the page knows those keys, so it is the page's `onClearAllMatching`.
 */
export function selectionIntent(ctx: SelectionIntentContext, intent: SelectionIntent): SelectionEffects {
    const nothing: SelectionEffects = { clearAllMatching: false, selectAllMatching: false, next: null };
    const server = ctx.mode === 'server';
    switch (intent.kind) {
        case 'row':
        case 'page': {
            const next: Set<SelectionKey> = intent.kind === 'row'
                ? toggleKey(ctx.effective, intent.key)
                : togglePage(ctx.effective, intent.keys);
            return { clearAllMatching: server && ctx.allMatchingSelected, selectAllMatching: false, next };
        }
        case 'selectAllMatching':
            if (server) return { ...nothing, selectAllMatching: true };
            return ctx.matchingKeys ? { ...nothing, next: addKeys(ctx.selectedKeys, ctx.matchingKeys) } : nothing;
        case 'clearAllMatching':
            if (server) return { ...nothing, clearAllMatching: true };
            return ctx.matchingKeys ? { ...nothing, next: removeKeys(ctx.selectedKeys, ctx.matchingKeys) } : nothing;
    }
}

/** `BulkActionBar` renders only while there is something to act on — an empty
 *  bar of disabled buttons is noise. */
export function bulkBarVisible(count: number): boolean {
    return count > 0;
}

/**
 * Fill `{name}` placeholders. The shared components take their copy as plain
 * strings (the tenant passes `t('key')` without variables, the admin passes a
 * Spanish literal), so they interpolate the counts themselves — same syntax as
 * the tenant's `t()`. Unknown placeholders are left as they are.
 */
export function fillTemplate(template: string, values: Readonly<Record<string, string | number>>): string {
    return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
        Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : whole,
    );
}

/* ── Pick ONE: the listbox of a search picker (EntitySelector) ─────────── */

/** A titled group of results. */
export interface ResultSection<O> {
    label: string;
    options: O[];
}

/** The one field every option has and no section has. */
interface HasId {
    id: string;
}

/** A section is an object with an `options` array and NO `id`. An option built
 *  by spreading an API record (`{...product, id, label, value}`) may carry an
 *  `options` field of its own; its `id` is what keeps it an option. */
function isSection(item: unknown): boolean {
    return typeof item === 'object' && item !== null && !('id' in item)
        && Array.isArray((item as { options?: unknown }).options);
}

/**
 * Normalise what a search returned — a flat list or titled sections — into
 * sections. A flat list becomes one untitled section; empty sections are
 * dropped (a title over nothing is noise). It is sections only when EVERY
 * entry is one: a mixed or ambiguous answer is read as the flat list it most
 * likely is, so no option is ever dropped or unpacked.
 */
export function toResultSections<O extends HasId>(results: readonly O[] | readonly ResultSection<O>[]): ResultSection<O>[] {
    if (results.length === 0) return [];
    const items: readonly unknown[] = results;
    if (items.every(isSection)) {
        return (results as readonly ResultSection<O>[]).filter(s => s.options.length > 0);
    }
    return [{ label: '', options: [...(results as readonly O[])] }];
}

/**
 * Where the highlight starts when a result list lands: on the current value
 * if it is among the options, else on the first option, else nowhere (-1).
 *
 * `optionIds` are the OPTIONS only. A trailing «create new» action is never
 * highlighted on its own: Enter on a list that has not answered yet (or has no
 * match) must not open the create flow — the user moves to it with ↓.
 */
export function initialActiveIndex(optionIds: readonly string[], valueId: string | null | undefined): number {
    if (optionIds.length === 0) return -1;
    const idx = valueId == null ? -1 : optionIds.indexOf(valueId);
    return idx >= 0 ? idx : 0;
}

/** ↓ (`+1`) / ↑ (`-1`) over `itemCount` stops (options + «create new»),
 *  clamped at both ends; from nowhere, either key lands on the first stop. */
export function stepActiveIndex(current: number, delta: 1 | -1, itemCount: number): number {
    if (itemCount <= 0) return -1;
    if (current < 0) return 0;
    return Math.max(0, Math.min(itemCount - 1, current + delta));
}

/**
 * Whether Enter may act on the highlighted stop. Never while the list on
 * screen does not answer the query in the box (`pending`: debounce running, a
 * request in flight, or the results of an earlier query): Enter would pick a
 * record of the OLD list and silently drop what the user just typed.
 */
export function enterPicks(activeIndex: number, pending: boolean): boolean {
    return !pending && activeIndex >= 0;
}

/**
 * The search picker's whole state machine — what it shows, which answer it
 * accepts and what is highlighted. `EntitySelector` only dispatches to it (and
 * owns focus, the portal and the timers), so «an answer that lands after the
 * list closed is dropped» is a tested rule, not a hope.
 */
export interface PickerState<O> {
    open: boolean;
    query: string;
    /** The request whose answer is awaited. `0` = none: closed, or the query
     *  is too short to search. An answer for any other id is dropped. */
    requestId: number;
    loading: boolean;
    failed: boolean;
    sections: ResultSection<O>[];
    /** The query `sections` answer (`null` = none yet). While it differs from
     *  `query` the list on screen is an OLD list. */
    answeredQuery: string | null;
    activeIndex: number;
}

export type PickerAction<O> =
    | { type: 'open' }
    | { type: 'close' }
    | { type: 'type'; query: string }
    /** A search for the current query started (ids only ever grow). */
    | { type: 'request'; id: number }
    /** The query is below `minQueryLength`: nothing to ask, nothing to show. */
    | { type: 'skip'; query: string }
    | { type: 'answer'; id: number; query: string; results: readonly O[] | readonly ResultSection<O>[]; valueId?: string | null }
    | { type: 'fail'; id: number; query: string }
    | { type: 'move'; delta: 1 | -1; itemCount: number }
    | { type: 'hover'; index: number };

export function pickerInitialState<O>(): PickerState<O> {
    return { open: false, query: '', requestId: 0, loading: false, failed: false, sections: [], answeredQuery: null, activeIndex: -1 };
}

/** Debounce running, request in flight, or the results of an earlier query:
 *  the list on screen does not answer the text in the box. */
export function pickerPending(state: Pick<PickerState<unknown>, 'loading' | 'answeredQuery' | 'query'>): boolean {
    return state.loading || state.answeredQuery !== state.query;
}

export function pickerReducer<O extends HasId>(state: PickerState<O>, action: PickerAction<O>): PickerState<O> {
    switch (action.type) {
        case 'open':
            return state.open ? state : { ...state, open: true };
        case 'close':
            // Everything goes, the awaited request included: an answer that
            // lands after this is dropped, so the next opening starts from a
            // spinner — never from the old list under an empty search box.
            return pickerInitialState<O>();
        case 'type':
            // The highlight belonged to the old list.
            return { ...state, query: action.query, activeIndex: -1 };
        case 'request':
            if (!state.open) return state;
            // Spinner from the first keystroke: an «empty» flash during the
            // debounce would read as «no such record».
            return { ...state, requestId: action.id, loading: true, failed: false };
        case 'skip':
            if (!state.open) return state;
            return { ...state, requestId: 0, loading: false, failed: false, sections: [], answeredQuery: action.query, activeIndex: -1 };
        case 'answer': {
            if (!state.open || action.id !== state.requestId) return state;
            const sections = toResultSections<O>(action.results);
            const ids = sections.flatMap(s => s.options.map(o => o.id));
            return {
                ...state,
                requestId: 0,
                loading: false,
                failed: false,
                sections,
                answeredQuery: action.query,
                activeIndex: initialActiveIndex(ids, action.valueId),
            };
        }
        case 'fail':
            if (!state.open || action.id !== state.requestId) return state;
            return { ...state, requestId: 0, loading: false, failed: true, sections: [], answeredQuery: action.query, activeIndex: -1 };
        case 'move':
            return { ...state, activeIndex: stepActiveIndex(state.activeIndex, action.delta, action.itemCount) };
        case 'hover':
            return state.activeIndex === action.index ? state : { ...state, activeIndex: action.index };
    }
}
